import {
  GetCheckboxData,
  UseCardsListSelectorProps,
} from "@shared/common/CardsList/types";
import { union } from "lodash";
import { useCallback, useMemo, useRef } from "react";
import { useRefValue } from "@shared/utils/hooks/misc";

const useCardsListSelector = <
  TableDataType extends object,
  RowKeyType extends string | number
>({
  selectedRows,
  setSelectedRows,
  getKey,
  allRows = [],
  filteredRows = [],
  openMessage,
  getRowSelectable,
}: UseCardsListSelectorProps<TableDataType, RowKeyType>) => {
  const validFilteredRows = useMemo(() => {
    return filteredRows?.filter((row) => !getRowSelectable?.(row));
  }, [filteredRows, getRowSelectable]);

  const isAllRowsSelected = useMemo(() => {
    return (
      !!validFilteredRows?.length &&
      validFilteredRows.every((row) => selectedRows.includes(getKey(row)))
    );
  }, [validFilteredRows, selectedRows]);

  const dataRef = useRef<
    | {
        allRows: TableDataType[];
        validFilteredRows: TableDataType[];
        selectedRows: RowKeyType[];
      }
    | undefined
  >(undefined);
  dataRef.current = { allRows, selectedRows, validFilteredRows };
  const selectedRowsSet = useMemo(() => new Set(selectedRows), [selectedRows]);
  const checkboxDataRef = useRefValue({
    selectedRowsSet,
    getRowSelectable,
    isAllRowsSelected,
    setSelectedRows,
  });

  const onSelectRow = (row: TableDataType, isSelected: boolean) => {
    let isValid = true;
    const errorMessage = checkboxDataRef.current.getRowSelectable?.(row);
    if (isSelected) {
      if (errorMessage) {
        openMessage?.(errorMessage, "warning");
        isValid = false;
      }
    }
    if (isValid) {
      onChangeSelectedRows([row], isSelected);
    }
  };

  const onSelectRowRef = useRefValue(onSelectRow);

  const handleSelectRows = (rows: TableDataType[]): RowKeyType[] => {
    return union(
      dataRef.current?.selectedRows,
      rows.map((data) => getKey(data))
    );
  };

  const handleUnselectRows = (rows: TableDataType[]) => {
    const rowKeysToUnselect = rows.map((membership) => getKey(membership));
    return (
      dataRef.current?.selectedRows.filter(
        (telegramId) => !rowKeysToUnselect.includes(telegramId)
      ) || []
    );
  };

  const onChangeSelectedRows = (
    rows: TableDataType[],
    isSelected?: boolean
  ) => {
    let updatedSelectedKeys: RowKeyType[];
    if (isSelected ?? !checkboxDataRef.current.isAllRowsSelected) {
      updatedSelectedKeys = handleSelectRows(rows);
    } else {
      updatedSelectedKeys = handleUnselectRows(rows);
    }
    checkboxDataRef.current.setSelectedRows?.(updatedSelectedKeys);
  };

  const onSelectAll = () => {
    const allKeys =
      dataRef.current?.validFilteredRows.map((row) => getKey(row)) || [];
    checkboxDataRef.current.setSelectedRows?.(union(allKeys, selectedRows));
  };

  const getCheckboxProps: GetCheckboxData<TableDataType> = useCallback(
    (row: TableDataType) => {
      const errorMessage = checkboxDataRef.current.getRowSelectable?.(row);
      const isSelected = selectedRowsSet.has(getKey(row));
      const onClick = () => {
        onSelectRowRef.current(row, !isSelected);
      };
      return {
        errorMessage,
        isSelected,
        onClick,
      };
    },
    [selectedRowsSet]
  );

  return {
    isAllRowsSelected,
    onSelectAll,
    onSelectRow,
    getCheckboxProps,
  };
};

export default useCardsListSelector;
